import React, { useEffect } from "react";
import { useHomePageContext } from "../homepage/HomePageContext";
import { AppDispatch, RootState } from "../../../store";
import { useDispatch } from "react-redux";
import { Product } from "../../../Redux/ecommersProduct/types";
import { useCart } from "../../../context/CartContext";
import {
  AddToCartRequest,
  CartType,
  ProductType,
} from "../../../Redux/custmoer_cart_service/types";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const RelatedProducts = ({ categoryName }: { categoryName: string | "" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const {
    productsByCategory,
    loadingByCategory,
    errorByCategory,
    fetchCategoryProducts,
  } = useHomePageContext();
  const products = productsByCategory[categoryName] || [];
  const loading = loadingByCategory[categoryName];
  const error = errorByCategory[categoryName];

  useEffect(() => {
    if (categoryName) {
      fetchCategoryProducts(categoryName);
    }
  }, []);

  const { addItemToCart, loading: cartLoading, error: cartError } = useCart();

  if (loading && products.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-yellow" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return null;
  }

  const handleAddToCart = async (currentProduct: Product) => {
    if (!isAuthenticated) {
      navigate("/SignIn");
      return;
    }
    const currentVariation = currentProduct.variations?.[0];
    const addToCartData: AddToCartRequest = {
      productId: currentProduct._id,
      productName: currentProduct.name,
      productImage: currentProduct.mainImage,
      productType: ProductType.PHYSICAL,
      variantId: currentVariation?.ssin,
      variantName: currentVariation?.sku,
      quantity: 1,
      unitPrice: currentVariation?.pricing?.finalPrice || 0,
      sellerId: currentProduct.creatorId,
      sellerName: currentProduct.vendors?.marketedBy || "",
      isMLMProduct: false,
      isAdminProduct: currentProduct.createdBy === "ADMIN" ? true : false,
    };

    try {
      const resultAction = await addItemToCart(addToCartData, CartType.ECOMMERCE);
      
      const addToCart_fulfilled = (action: any) => action.type.endsWith("/fulfilled");

      if (addToCart_fulfilled(resultAction)) {
        // Automatically open the side cart
        const offcanvasElement = document.getElementById("offcanvasMiniCart");
        if (offcanvasElement) {
          const bootstrap = (window as any).bootstrap;
          if (bootstrap && (window as any).bootstrap.Offcanvas) {
            const bsOffcanvas = new (window as any).bootstrap.Offcanvas(
              offcanvasElement,
            );
            bsOffcanvas.show();
          }
        }
      } else {
        alert("Failed to add item to cart");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
    }
  };

  return (
    <>
      <div className=" display-max">
        <div className={`container ${products.length > 0 ? "" : "d-none"}`}>
          <div className=" d-flex justify-content-start align-items-center gap-2 px-3 overflow-auto hide-scrollbar">
            {products.slice(0, 6).map((product) => {
              const variation = product.variations?.[0];
              const pricing = variation?.pricing;
              const unitPrice = pricing?.unitPrice || 0;
              const finalPrice = pricing?.finalPrice || unitPrice;
              const discount =
                unitPrice > finalPrice
                  ? Math.round(((unitPrice - finalPrice) / unitPrice) * 100)
                  : 0;

              return (
                <div className=" col-lg-2 flex-shrink-0" key={product._id}>
                  <div className="card px-2 border-radius-0 shadow-none mb-lg-0 h-100">
                    <a href={`/DetailPage/${product._id}`}>
                      <div className="card-header p-0 position-relative border-radius-00 z-index-2 bg-gray">
                        <div className="d-block blur-shadow-image text-center">
                          <img
                            src={product.mainImage}
                            alt={product.name}
                            className="img-fluid-max-screens p-3"
                            style={{ height: "150px", objectFit: "contain" }}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src =
                                "../../assets/img/salarlogo.png";
                            }}
                          />
                        </div>
                      </div>
                    </a>
                    <div className="p-2 text-start lh-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex justify-content-between align-items-start gap-1">
                          <h6
                            className="text-dark fw-bold mb-1 text-truncate-2"
                            style={{
                              fontSize: "0.875rem",
                              minHeight: "2.5rem",
                            }}
                          >
                            <a
                              href={`/DetailPage/${product._id}`}
                              className="text-dark text-decoration-none"
                            >
                              {product.name}
                            </a>
                          </h6>
                          <span className="text-black text-xs flex-shrink-0 d-flex align-items-center">
                            <i
                              className="bi bi-star-fill me-1 text-warning"
                              style={{ fontSize: "10px" }}
                            ></i>
                            {(product.reviews?.averageRating || 0).toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <div className=" mt-1">
                        <div className=" lh-1">
                          <b
                            className="mt-2 fw-normal text-xxs text-black text-truncate-2"
                            style={{ minHeight: "2.5em" }}
                          >
                            {product.description}
                          </b>
                        </div>
                      </div>
                    </div>
                    <div className=" d-flex justify-content-between align-items-center mb-2 px-2 mt-auto">
                      <div>
                        <b className="font-bold fs-5 text-black my-auto">
                          <sup className=" fw-normal">₹</sup>
                          {finalPrice}{" "}
                          <sub>
                            <b className="fw-normal text-black text-xxs">
                              MRP:{" "}
                            </b>{" "}
                            <b className="fw-normal text-black text-xxs text-decoration-line-through">
                              {unitPrice}
                            </b>
                            {discount > 0 && (
                              <b className="fw-bold text-danger text-xs ms-1">
                                ({discount}%Off){" "}
                              </b>
                            )}
                          </sub>
                        </b>
                      </div>
                      <div>
                        <button
                          className=" btn btn-sm btn-yellow"
                          data-bs-toggle={!isAuthenticated ? "" : "offcanvas"}
                          data-bs-target={!isAuthenticated ? "" : "#offcanvasMiniCart"}
                          aria-controls={!isAuthenticated ? "" : "offcanvasMiniCart"}
                          onClick={() => handleAddToCart(product)}
                          disabled={cartLoading}
                        >
                          {cartLoading ? "Adding..." : "Add "}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className=" displa-mini display-mob">
        <div className="mt-3 container mx-auto d-flex justify-content-between align-items-center px-3 mb-3">
          <div>
            <b className="fs-6 text-black">
              Home and Kitchen Appliances{" "}
              <i className="ri-arrow-right-s-line"></i>
            </b>
          </div>
        </div>
        <div className=" d-flex justify-content-start align-items-center px-1 overflow-sides mb-3">
          {products.slice(0, 6).map((product) => {
            const variation = product.variations?.[0];
            const price = variation?.pricing;
            const unitPrice = price?.unitPrice || 0;
            const finalPrice = price?.finalPrice || unitPrice;
            const discount =
              unitPrice > finalPrice
                ? Math.round(((unitPrice - finalPrice) / unitPrice) * 100)
                : 0;

            return (
              <div className=" col-6" key={product._id}>
                <div className="card px-1 border-radius-0 shadow-none mb-lg-0 h-100">
                  <a href={`/DetailPage/${product._id}`}>
                    <div className="card-header p-0 position-relative border-radius-00 z-index-2 bg-gray">
                      <div className="d-block blur-shadow-image text-center">
                        <img
                          src={product.mainImage}
                          alt={product.name}
                          className="img-fluid-mini-screens p-1"
                          style={{ height: "120px", objectFit: "contain" }}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src =
                              "../../assets/img/salarlogo.png";
                          }}
                        />
                      </div>
                    </div>
                  </a>
                  <div className="p-2 text-start lh-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex justify-content-between align-items-start gap-1">
                        <h6
                          className="text-dark fw-bold mb-1 text-truncate-2"
                          style={{ fontSize: "0.875rem", minHeight: "2.5rem" }}
                        >
                          <a
                            href={`/DetailPage/${product._id}`}
                            className="text-dark text-decoration-none"
                          >
                            {product.name}
                          </a>
                        </h6>
                        <span className="text-black text-xs flex-shrink-0 d-flex align-items-center">
                          <i
                            className="bi bi-star-fill me-1 text-warning"
                            style={{ fontSize: "10px" }}
                          ></i>
                          {(product.reviews?.averageRating || 4.0).toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <div className=" mt-1">
                      <div className=" lh-1">
                        <b
                          className="mt-1 fw-normal text-xxs text-black text-truncate-2"
                          style={{ minHeight: "2.5em" }}
                        >
                          {product.description}
                        </b>
                      </div>
                    </div>
                  </div>
                  <div className=" d-flex justify-content-between align-items-end mb-2 mt-2 px-2 mt-auto">
                    <div className=" lh-1">
                      <b className="font-bold fs-5 text-black my-auto">
                        <sup className=" fw-normal">₹</sup>
                        {finalPrice}{" "}
                      </b>
                      <div>
                        {" "}
                        <sub>
                          <b className="fw-normal text-black text-xxs">MRP: </b>{" "}
                          <b className="fw-normal text-black text-xxs text-decoration-line-through">
                            {unitPrice}
                          </b>
                        </sub>
                      </div>
                    </div>
                    <div>
                      <button
                        className=" btn btn-sm btn-yellow"
                        data-bs-toggle="offcanvas"
                        data-bs-target="#offcanvasMiniCart"
                        aria-controls="offcanvasMiniCart"
                        onClick={() => handleAddToCart(product)}
                        disabled={cartLoading}
                      >
                        {cartLoading ? "Adding..." : "Add "}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default RelatedProducts;
